// Personal space — the signed-in employee's own view (reached via the sidebar avatar).
// Faithful port of the Claude Design "My space" prototype (screens_me.jsx): identity
// header, editable contact + bank details, apply-for-leave, and the leave calendar.
//
// Everything on this screen is live: identity from the auth profile, contact and
// bank from employee_contact / employee_bank, leave from leave_types +
// leave_balances + leave_requests, and the timesheet from time_entries. There is
// no demo fallback and nothing persists to localStorage — an incomplete HR record
// renders as blank, never as another employee's details.
import {
  Fragment,
  useCallback,
  useEffect,
  useId,
  useMemo,
  useReducer,
  useRef,
  useState,
  type CSSProperties,
  type FormEvent,
  type KeyboardEvent,
  type ReactNode,
} from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '../lib/supabase';
import { useAuth } from '../lib/auth';
import { can, canOpen, roleLabel, roleTone } from '../lib/roles';
import { isWorkday, workingDayList, workingDays, type Workdays } from '../lib/workdays';
import { CONTACT_SELECT, contactCols } from '../lib/contact';
import { write, type WriteResult } from '../lib/write';
import { DEFAULT_ORG_HOURS } from '../lib/orgHours';
import { loadSummaries, type MonthSummary } from '../lib/monthlySummary';
import { useOrgDates } from '../lib/orgSettings';
import { useCompOff } from '../lib/compOff';
import { MyPayslip } from '../components/payroll';
import { CompHoursDecisions, MyRequests } from '../components/requests';
import { TeamDaysToFix } from '../components/fixDay';
import { TeamDayEdits } from '../components/editDay';
import { CompHoursModal } from '../components/compHours';
import { listDirectReports, type DirectReport } from '../lib/directReports';
import { MyDailyReport, TeamReports } from '../components/dailyReport';
import {
  listForManager,
  listCategories,
  listMine as listMyComp,
  type CompHours,
  type PendingComp,
  type Category,
} from '../lib/compHours';
import { listMine as listMyCorrections, type Correction } from '../lib/timeCorrections';
import {
  addDays,
  fmtDate,
  fmtDayKey,
  fmtDayRange,
  isoDay,
  monthRange,
  orgDateKey,
  orgToday,
  weekColumns,
  weekHeader,
  weekStart,
  weekdayLabels,
} from '../lib/dates';
import { fmtDuration } from '../lib/duration';
import { Avatar, Badge, Button, Checkbox, Icon, TONE, markColor } from '../components/primitives';
import { Eyebrow, PeriodStepper, Surface } from '../components/card';
import { KpiTile } from '../components/domain';
import { Dialog } from '../components/dialog';
import { DayMark, DayMarkKey, MonthGrid, dayMarkStyle } from '../components/monthGrid';
import type { Holiday } from '../lib/holidays';
import { useBelow } from '../lib/useMediaQuery';
import { DateField, Field, TextArea, type SaveResult } from '../components/field';
import { EmptyState, InlineStatus, LoadError, useToast } from '../components/feedback';
import { Markdown } from '../components/Markdown';
import { PCard, EditableField, MONTHS, SensitiveMark, maskAcct } from '../components/people';
import { MyPoliciesCard } from '../components/policies';
import { CertificatesCard } from '../components/certificates';

// Leave types, balances and the calendar legend are loaded live from the DB
// (leave_types / leave_balances) inside MeScreen — see loadLeave().

// Every date on this screen goes through fmtDate / weekHeader (lib/dates), not
// the private DD-MM-YYYY helper that sat beside a browser-locale "Oct 12" and a
// "10/15/2026" picker (ME-13); fmtDate takes the org's date_format from
// useOrgDates().
/** Hours as the one summed-figure shape, "2.5" — the header said "3h" over a 2.5 cell (ME-14). */
const hrs = (h: number) => fmtDuration(h * 3600, 'decimal');

// employee_bank's own columns, so a save names the one column it changes.
type Bank = { holder: string; account_no: string; ifsc: string; bank_name: string; branch: string };
const EMPTY_BANK: Bank = { holder: '', account_no: '', ifsc: '', bank_name: '', branch: '' };

/**
 * A bank value as it will be stored, or why it can't be. There was no check at
 * all, on the field a salary follows (ME-05). Shape only, and wide enough for an
 * IBAN or a CLABE: whether the account exists is the bank's to say.
 */
const cleanBank = (col: keyof Bank, raw: string): { value: string } | { error: string } => {
  if (col === 'account_no') {
    const v = raw.replace(/[\s-]/g, '').toUpperCase();
    return !v || /^[A-Z0-9]{6,34}$/.test(v)
      ? { value: v }
      : { error: 'An account number is 6 to 34 letters and digits.' };
  }
  if (col === 'ifsc') {
    const v = raw.replace(/\s/g, '').toUpperCase();
    return !v || /^[A-Z0-9]{8,11}$/.test(v)
      ? { value: v }
      : { error: 'A bank code is 8 to 11 letters and digits: an IFSC or a SWIFT/BIC.' };
  }
  return { value: raw.trim() };
};

/** What a write that didn't land says on its field. Zero rows is a refusal, not a save (lib/write). */
const notSaved = (r: Exclude<WriteResult, { ok: true }>): SaveResult => ({
  error: r.reason === 'refused' ? 'You can’t change this.' : `Couldn’t save: ${r.error.message}`,
});
// Spans two tables: the private PII on employee_contact, and location / joined /
// job_description on profiles. That is why patchContact routes each key and
// filters the employee_contact write through contactCols().
// Module scope, not inside the component — it is referenced from a useEffect.
type Contact = {
  mobile: string | null;
  personal_email: string | null;
  address: string | null;
  dob: string | null;
  emergency_name: string | null;
  emergency_relation: string | null;
  emergency_phone: string | null;
  location: string | null;
  joined: string | null;
  job_description: string | null;
  employee_no: number | null;
};
/** Two short fields side by side — the contact card's whole compaction trick.
 *  Folds to one column when the card is narrow, instead of clipping (ME-01). */
const pair: CSSProperties = {
  display: 'grid',
  gridTemplateColumns: 'repeat(auto-fit, minmax(min(100%, 12rem), 1fr))',
  gap: 'var(--hf-space-4)',
};

/** One line under a card's title saying how its fields behave. */
const cardHint: CSSProperties = {
  margin: 0,
  fontSize: 'var(--hf-text-xs)',
  lineHeight: 'var(--hf-leading-snug)',
  color: 'var(--hf-ink-3)',
};

// ── a card body that waits for its read ───────────────────────────────────────
// Every reader here did `data ?? []`, so a failed request drew a blank contact
// record, "No hours logged this week yet" and an empty bank card that would
// then save over the real one. A failed read is not an empty record
// (UI-CONVENTIONS §7), and neither is one that hasn't answered yet.
type Read = 'loading' | 'ready' | 'error';
const Loaded = ({
  read,
  what,
  onRetry,
  children,
}: {
  read: Read;
  /** After "Couldn’t load": "your contact details". */
  what: string;
  onRetry: () => void;
  children?: ReactNode;
}) =>
  read === 'error' ? (
    <LoadError what={what} onRetry={onRetry} />
  ) : read === 'loading' ? (
    // Plain text, not a live region: seven cards load at once.
    <p style={cardHint}>Loading…</p>
  ) : (
    <>{children}</>
  );

/** A column of cards. minWidth 0, so a long word inside one can't widen the track. */
const stack: CSSProperties = {
  display: 'flex',
  flexDirection: 'column',
  gap: 'var(--hf-space-4)',
  minWidth: 0,
};

const EMPTY_CONTACT: Contact = {
  mobile: null,
  personal_email: null,
  address: null,
  dob: null,
  emergency_name: null,
  emergency_relation: null,
  emergency_phone: null,
  location: null,
  joined: null,
  job_description: null,
  employee_no: null,
};
type Balance = { allotted: number; used: number };
type MeRecord = {
  name: string;
  role: string; // job title
  dept: string;
  systemRole: string;
  id: string; // employee code
  location: string;
  mobile: string;
  email: string;
  personalEmail: string;
  address: string;
  dob: string;
  emergencyName: string;
  emergencyRelation: string;
  emergencyPhone: string;
  bank: Bank;
  joined: string;
  jd: string;
};

// null when HR hasn't set a join date yet — otherwise new Date('') gives an
// Invalid Date and the header renders "NaN yr NaN mo". `now` is the org's today.
const tenure = (joined: string, now: Date): { yrs: number; mos: number } | null => {
  if (!joined) return null;
  const j = new Date(`${joined}T00:00:00`);
  if (Number.isNaN(j.getTime())) return null;
  let yrs = now.getFullYear() - j.getFullYear();
  let mos = now.getMonth() - j.getMonth();
  // Day-of-month was ignored, so someone who joined on the 28th read "1 mo" four
  // days later — and the sub-caption right below it showed the actual join date,
  // so the two visibly disagreed.
  if (now.getDate() < j.getDate()) mos--;
  if (mos < 0) {
    yrs--;
    mos += 12;
  }
  return { yrs, mos };
};

// ── identity-header stat block ────────────────────────────────────────────────
/** The small mono word after a display numeral: "3 yr", "16 annual". */
// KpiTile's own unit size, so "8 casual" and "2 yr" read like "0 h" beside them.
const statUnit: CSSProperties = {
  fontSize: 'var(--hf-text-sm)',
  color: 'var(--hf-ink-3)',
  fontFamily: 'var(--hf-font-mono)',
  marginLeft: 'var(--hf-space-1)',
};
const headerFact: CSSProperties = {
  display: 'inline-flex',
  alignItems: 'center',
  gap: 'var(--hf-space-2)',
  minWidth: 0,
};
// ── account number: masked until asked for ───────────────────────────────────
// The editor sat behind a lowercase 10px "reveal", and with no number saved the
// row was an empty line, so a new employee had nowhere to type one (ME-05). With
// nothing to hide it is simply the field; otherwise a named button shows it.
const BankAccount = ({
  value,
  onSave,
}: {
  value: string;
  onSave: (v: string) => Promise<SaveResult>;
}) => {
  const [show, setShow] = useState(false);
  const open = show || !value;
  return (
    <div
      style={{
        display: 'flex',
        alignItems: 'flex-end',
        flexWrap: 'wrap',
        gap: 'var(--hf-space-2)',
      }}
    >
      <div style={{ flex: '1 1 12rem', minWidth: 0 }}>
        {/* Keyed, so hiding the number can't leave a half-typed draft behind it. */}
        <EditableField
          key={open ? 'open' : 'masked'}
          label="Account number or IBAN"
          value={open ? value : maskAcct(value)}
          mono={!!value}
          placeholder="Add account number"
          commitOn="explicit"
          onSaveAsync={
            open
              ? async (v) => {
                  const r = await onSave(v);
                  // The first number saved stays on screen with its "Saved",
                  // rather than masking itself the moment it lands.
                  if (!r.error) setShow(true);
                  return r;
                }
              : undefined
          }
        />
      </div>
      {value && (
        <Button
          variant="ghost"
          size="sm"
          icon={show ? 'eyeOff' : 'eye'}
          onClick={() => setShow((x) => !x)}
          className="hf-hit"
        >
          {show ? 'Hide' : 'Show to edit'}
        </Button>
      )}
    </div>
  );
};

// ── apply-for-leave form ──────────────────────────────────────────────────────
type ApplyPayload = {
  type: string;
  start: string;
  end: string;
  reason: string;
  dayCount: number;
  halfDay: boolean;
};
type FormLeaveType = {
  id: string;
  label: string;
  color: string;
  /**
   * An unpaid day keeps its required hours — person_required_hours skips it
   * (20260829120000_payroll.sql:129) — so it lands in shortfall_hours and is
   * priced at shortfall / required × gross. Choosing one costs salary, and this
   * form is where the choice is made. It used to drop the flag entirely, so the
   * only place the word "unpaid" appeared was HR's view of a request already
   * filed.
   */
  paid: boolean;
};

/**
 * Why a leave type can't be chosen, or null when it can. A paid type with
 * nothing left is one HR cannot approve (enforce_leave_request refuses at
 * approval), so offering it only queues a request that is going to fail —
 * "Casual 0/0 left" used to be selectable (ME-06). An unpaid type has no
 * entitlement to run out of.
 */
const unavailable = (t: FormLeaveType, b: Balance | undefined): string | null => {
  if (!t.paid) return null;
  if (!b || b.allotted <= 0) return 'no days allotted';
  return b.allotted - b.used <= 0 ? 'none left this year' : null;
};

type FormErrors = Partial<Record<'type' | 'start' | 'end' | 'reason', string>>;
/** The message under a control Field can't wrap (the radiogroup): Field's own error line. */
const errorLine: CSSProperties = {
  fontSize: 'var(--hf-text-xs)',
  lineHeight: 'var(--hf-leading-snug)',
  color: 'var(--hf-rose-text)',
};
const nDays = (n: number) => `${n} day${n !== 1 ? 's' : ''}`;

const ApplyLeaveForm = ({
  onSubmit,
  balances,
  types,
  workdays,
  holidays,
  canBackdate,
  today,
}: {
  onSubmit: (p: ApplyPayload) => Promise<{ error?: string }>;
  balances: Record<string, Balance>;
  types: FormLeaveType[];
  workdays?: Workdays | null;
  holidays?: ReadonlySet<string>;
  // HR may record leave taken offline; everyone else is held to today onward.
  // The DB enforces this (against the org timezone) — this only shapes the UI.
  canBackdate: boolean;
  /** The ORG's today as a date key (orgDateKey) — the day the server compares
   *  against. It was the browser's, a day off for anyone outside the org's zone (ME-13). */
  today: string;
}) => {
  const toast = useToast();
  const id = useId();
  // Below md each choice gets a row of its own and Submit spans the form, where
  // a thumb is: two 9rem tiles a row wrapped "4/8 left · 4 used" onto two lines.
  const phone = useBelow('md');
  const form = useRef<HTMLFormElement>(null);
  const [picked, setPicked] = useState('');
  const [start, setStart] = useState('');
  const [end, setEnd] = useState('');
  const [reason, setReason] = useState('');
  const [busy, setBusy] = useState(false);
  const [halfDay, setHalfDay] = useState(false);
  // Each message belongs to a field, and sits beside it: they used to appear
  // under the Submit button with nothing marking the field at fault (ME-06).
  const [errors, setErrors] = useState<FormErrors>({});
  // A refusal from the server that no single field explains.
  const [failed, setFailed] = useState<string | null>(null);
  const focusInvalid = useRef(false);

  // The first type that can be taken, until one is picked. Derived, so a type
  // that runs out while the form is open stops being the selection.
  const open = types.filter((t) => !unavailable(t, balances[t.id]));
  const type = open.some((t) => t.id === picked) ? picked : (open[0]?.id ?? '');
  const chosen = types.find((t) => t.id === type);

  // Same rule the server applies (org workdays + holidays), so the number shown
  // here is the number that gets deducted.
  const dayCount = useMemo(
    () => workingDays(start, end || start, workdays, holidays),
    [start, end, workdays, holidays],
  );

  // Half-day only applies to a single-day request; a range turns it off.
  const singleDay = !!start && (!end || end === start);
  const half = halfDay && singleDay;
  const displayDays = half ? 0.5 : dayCount;
  // The dates are set in one place so the tick is DROPPED when they stop being
  // a single day, not only hidden. Hidden, it came back by itself: after a
  // half-day request the form cleared, and the next From date re-ticked the box
  // and filed a full day as `half_day: true`.
  const setDates = (from: string, to: string) => {
    setStart(from);
    setEnd(to);
    if (!from || (to && to !== from)) setHalfDay(false);
  };

  // Asking for more than is left is said, not refused. The server checks the
  // balance when HR approves, not when you file (enforce_leave_request), and it
  // can rise in between: HR adjusts it, a leave is reverted. Only a type with nothing
  // left at all is closed (`unavailable`).
  const chosenBalance = chosen?.paid ? balances[chosen.id] : undefined;
  const left = chosenBalance ? chosenBalance.allotted - chosenBalance.used : 0;
  const short =
    chosen?.paid && dayCount >= 1 && displayDays > left
      ? `Only ${nDays(left)} of ${chosen.label.toLowerCase()} leave left, and these dates need ${displayDays}. You can still submit: HR can approve it only if your balance is raised.`
      : null;

  // After a failed submit, go to the first field that needs fixing.
  useEffect(() => {
    if (!focusInvalid.current) return;
    focusInvalid.current = false;
    form.current
      ?.querySelector<HTMLElement>(
        '[aria-invalid="true"] [role="radio"][tabindex="0"], input[aria-invalid="true"], textarea[aria-invalid="true"]',
      )
      ?.focus();
  }, [errors]);

  const clear = (k: keyof FormErrors) => setErrors((e) => (e[k] ? { ...e, [k]: undefined } : e));

  const validate = (): FormErrors => {
    const e: FormErrors = {};
    if (!chosen) {
      e.type = types.length
        ? 'None of your leave types has days left. Ask HR.'
        : 'No leave types are set up yet. Ask HR.';
    }
    if (!start) e.start = 'Pick the first day of your leave.';
    else if (!canBackdate && start < today)
      e.start = 'Leave can’t start in the past. Ask HR to record it.';
    else if (end && end < start) e.end = 'The last day is before the first.';
    else if (dayCount < 1) e.start = 'Those dates are all weekends or holidays.';
    if (!reason.trim()) e.reason = 'Say what the leave is for.';
    return e;
  };

  const submit = async (ev: FormEvent) => {
    ev.preventDefault();
    if (busy) return;
    setFailed(null);
    const found = validate();
    if (Object.keys(found).length || !chosen) {
      focusInvalid.current = true;
      setErrors(found);
      return;
    }
    setErrors({});
    setBusy(true);
    const { error } = await onSubmit({
      type,
      start,
      end: end || start,
      reason: reason.trim(),
      dayCount,
      halfDay: half,
    });
    setBusy(false);
    if (error) {
      // The two refusals that are about the dates go to the date field; the
      // rest are shown as the server put them. It was always the raw string
      // after "Could not submit:".
      if (/past date/.test(error)) {
        focusInvalid.current = true;
        setErrors({ start: 'Leave can’t start in the past. Ask HR to record it.' });
      } else if (/no working days/.test(error)) {
        focusInvalid.current = true;
        setErrors({ start: 'Those dates are all weekends or holidays.' });
      } else setFailed(error);
      return;
    }
    // Say it once more on the way out. The consequence of an unpaid day is a
    // smaller payslip a month later, which is a long way from the click. Still
    // a success — the request did submit; the amber warning that matters sits
    // on the type itself, at the moment of choosing.
    toast.push({
      tone: 'success',
      message:
        `Request submitted: ${nDays(displayDays)} of ${chosen.label.toLowerCase()} leave.` +
        (chosen.paid
          ? ''
          : ' Unpaid: these days count as a shortfall on the payroll for the month they fall in, and can reduce that month’s pay.'),
    });
    setDates('', '');
    setReason('');
  };

  // The radiogroup contract (the SegmentedControl one): one Tab stop, arrows
  // and Home/End move and choose, skipping types that can't be taken.
  const onTypeKeys = (e: KeyboardEvent<HTMLDivElement>) => {
    const step = { ArrowRight: 1, ArrowDown: 1, ArrowLeft: -1, ArrowUp: -1 }[e.key];
    const n = open.length;
    const at = open.findIndex((t) => t.id === type);
    const next =
      step != null ? (at + step + n) % n : e.key === 'Home' ? 0 : e.key === 'End' ? n - 1 : -1;
    if (next < 0 || !n) return;
    e.preventDefault();
    setPicked(open[next].id);
    clear('type');
    e.currentTarget.querySelector<HTMLElement>(`[data-type="${open[next].id}"]`)?.focus();
  };

  // `min` keeps the past off the picker for staff; HR gets an open one. Purely
  // UX — enforce_leave_request() is the boundary and uses the org timezone.
  const minDate = canBackdate ? undefined : today;
  const typeLabelId = `${id}-type`;
  const typeErrorId = `${id}-type-error`;
  const typeNoteId = `${id}-type-note`;

  return (
    <form
      ref={form}
      noValidate
      onSubmit={(e) => void submit(e)}
      style={{ display: 'flex', flexDirection: 'column', gap: 'var(--hf-space-4)' }}
    >
      <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--hf-space-2)' }}>
        <Eyebrow as="div" id={typeLabelId}>
          Leave type
        </Eyebrow>
        {/* Radios, not plain buttons: the choice between a paid and an unpaid
            type was exposed as a coloured glow and nothing else (ME-06). */}
        <div
          role="radiogroup"
          aria-labelledby={typeLabelId}
          aria-describedby={errors.type ? typeErrorId : short ? typeNoteId : undefined}
          aria-invalid={errors.type ? true : undefined}
          aria-required="true"
          onKeyDown={onTypeKeys}
          style={{
            display: 'grid',
            // 16rem on a phone: one tile a row at 390, two on a large phone.
            gridTemplateColumns: `repeat(auto-fit, minmax(min(100%, ${phone ? '16rem' : '9rem'}), 1fr))`,
            gap: 'var(--hf-space-2)',
          }}
        >
          {types.map((t) => {
            const active = t.id === type;
            const b = balances[t.id];
            const allot = b ? b.allotted : 0;
            const used = b ? b.used : 0;
            const bal = allot - used;
            const why = unavailable(t, b);
            // An unpaid type is uncapped by design — the server skips the
            // entitlement ceiling for it — so `allotted` is always 0 and this
            // printed "-3/0 left" with an empty bar: a negative balance for
            // something that cannot be overdrawn, and no hint that it costs pay.
            const rem = !t.paid || !allot ? 0 : Math.max(0, Math.min(1, bal / allot));
            return (
              <button
                key={t.id}
                type="button"
                role="radio"
                aria-checked={active}
                data-type={t.id}
                // 44px tall under a finger (tokens.css: a radio .hf-hit grows).
                className="hf-hit"
                disabled={!!why}
                tabIndex={active ? 0 : -1}
                onClick={() => {
                  setPicked(t.id);
                  clear('type');
                }}
                style={{
                  position: 'relative',
                  overflow: 'hidden',
                  display: 'flex',
                  alignItems: 'center',
                  gap: 'var(--hf-space-2)',
                  padding: 'var(--hf-space-2) var(--hf-space-3)',
                  borderRadius: 'var(--hf-radius-md)',
                  cursor: why ? 'not-allowed' : 'pointer',
                  textAlign: 'left',
                  font: 'inherit',
                  // The earlier tile (owner, 2026-10-09: "use this style"): the
                  // whole tile washed in the type's colour, chosen in its own
                  // colour with a soft glow, not the accent and a radio dot.
                  background: why
                    ? 'transparent'
                    : `color-mix(in srgb, ${t.color} ${active ? 10 : 6}%, var(--hf-surface))`,
                  border: why
                    ? '1px dashed var(--hf-rule-2)'
                    : `1px solid color-mix(in srgb, ${t.color} ${active ? 80 : 28}%, transparent)`,
                  boxShadow: active
                    ? `0 0 0 1px color-mix(in srgb, ${t.color} 60%, transparent), 0 0 14px color-mix(in srgb, ${t.color} 30%, transparent)`
                    : 'none',
                }}
              >
                {/* How much is left, as a fill. Scaled, not resized: a width
                    transition is a layout change on every frame. */}
                <span
                  aria-hidden="true"
                  style={{
                    position: 'absolute',
                    inset: 0,
                    transform: `scaleX(${rem})`,
                    transformOrigin: 'left',
                    background: `color-mix(in srgb, ${t.color} ${active ? 16 : 10}%, transparent)`,
                    // Where what is left ends: the line in the earlier tile.
                    borderRight:
                      rem > 0 && rem < 1
                        ? `1px solid color-mix(in srgb, ${t.color} 55%, transparent)`
                        : undefined,
                    pointerEvents: 'none',
                    transition: 'transform var(--hf-dur-layout) var(--hf-ease)',
                  }}
                />
                <span style={{ position: 'relative', flex: 1, minWidth: 0 }}>
                  <span
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 'var(--hf-space-2)',
                      fontSize: 'var(--hf-text-sm)',
                      fontWeight: 'var(--hf-weight-medium)',
                      color: why ? 'var(--hf-ink-3)' : active ? 'var(--hf-ink)' : 'var(--hf-ink-2)',
                    }}
                  >
                    {/* The type's mark as the calendar's key draws it: the raw
                        colour was a pale cyan here over a dark teal key. */}
                    <span aria-hidden="true" style={dayMarkStyle('leave', t.color)} />
                    {t.label}
                  </span>
                  <span
                    style={{
                      display: 'block',
                      fontSize: 'var(--hf-text-2xs)',
                      color: t.paid ? 'var(--hf-ink-3)' : 'var(--hf-amber-text)',
                      // Figures in mono, as the earlier tile set them; words
                      // ("can reduce pay") stay in the UI face.
                      fontFamily: t.paid && !why ? 'var(--hf-font-mono)' : undefined,
                      fontVariantNumeric: 'tabular-nums',
                    }}
                  >
                    {/* A line breaks only at the ' · ': three across at 1440,
                        the 11px floor split "6 used" and "reduce pay". */}
                    {(!t.paid
                      ? ['unpaid', 'can reduce pay']
                      : why
                        ? [why]
                        : [`${bal}/${allot} left`, `${used} used`]
                    ).map((phrase, i) => (
                      <Fragment key={i}>
                        {i > 0 && ' · '}
                        <span style={{ whiteSpace: why ? undefined : 'nowrap' }}>{phrase}</span>
                      </Fragment>
                    ))}
                  </span>
                </span>
              </button>
            );
          })}
        </div>
        {errors.type && (
          <div id={typeErrorId} role="alert" style={errorLine}>
            {errors.type}
          </div>
        )}
      </div>
      <div
        style={{
          display: 'grid',
          // A date field at the phone's 16px needs ~14rem, picker icon included.
          gridTemplateColumns: `repeat(auto-fit, minmax(min(100%, ${phone ? '14rem' : '9rem'}), 1fr))`,
          gap: 'var(--hf-space-3)',
          alignItems: 'start',
        }}
      >
        {/* DateField echoes each day in the org's format: these were bare
            inputs reading mm/dd/yyyy under "joined 01-03-2022" (critique
            2026-10-05 #2, ME-13). The whole range is said again by Submit. */}
        <DateField
          label="From"
          required
          error={errors.start}
          value={start}
          min={minDate}
          onChange={(v) => {
            setDates(v, end);
            clear('start');
          }}
        />
        <DateField
          label="To"
          hint="Leave empty for one day."
          error={errors.end}
          value={end}
          min={start || minDate}
          onChange={(v) => {
            setDates(start, v);
            clear('end');
            clear('start');
          }}
        />
      </div>
      {/* Always rendered, disabled when the range is more than a day.
          `singleDay` is `!!start && (!end || end === start)`, so hiding it meant
          the control did not exist until you had picked a From date AND left To
          empty — fill both in, which is the obvious thing to do, and half-day
          leave appeared nowhere in the product. It was reported as "leave can
          only be applied for full day". The server refuses a multi-day half-day
          anyway ('a half-day leave must be a single day'), so the disabled state
          says what the database would say, before the round-trip. */}
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          flexWrap: 'wrap',
          gap: 'var(--hf-space-2)',
        }}
      >
        <Checkbox
          checked={half}
          disabled={!singleDay}
          onChange={setHalfDay}
          label="Half day (0.5)"
        />
        {!singleDay && (
          <span style={{ fontSize: 'var(--hf-text-xs)', color: 'var(--hf-ink-3)' }}>
            {start ? 'One day only: clear “To”.' : 'Pick a date first.'}
          </span>
        )}
      </div>
      <Field
        label="Reason"
        required
        hint="Required. HR sees it with your request."
        error={errors.reason}
      >
        <TextArea
          rows={2}
          value={reason}
          onChange={(v) => {
            setReason(v);
            clear('reason');
          }}
          placeholder="e.g. Family trip"
        />
      </Field>
      {/* By Submit, not under the types it describes: there it opened above the
          date fields and moved them while a date was being picked. */}
      {short && (
        <div id={typeNoteId}>
          <InlineStatus tone="warning">{short}</InlineStatus>
        </div>
      )}
      {failed && <InlineStatus tone="error">Your request wasn’t submitted: {failed}</InlineStatus>}
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          flexWrap: 'wrap',
          gap: 'var(--hf-space-3)',
        }}
      >
        {/* The request in one line, read once before it is sent: the days
            spelled out, so a range picked in the wrong month or year shows
            here, and the length the server will charge. */}
        <span
          aria-live="polite"
          style={{ fontSize: 'var(--hf-text-xs)', color: 'var(--hf-ink-2)' }}
        >
          {[
            fmtDayRange(start, end || undefined, today),
            displayDays > 0 && `${displayDays} working day${displayDays !== 1 ? 's' : ''}`,
          ]
            .filter(Boolean)
            .join(' · ')}
        </span>
        <div style={{ flex: 1 }} />
        <Button
          type="submit"
          variant="primary"
          size={phone ? 'xl' : 'md'}
          full={phone}
          icon="leave"
          loading={busy}
        >
          Submit request
        </Button>
      </div>
    </form>
  );
};

// ── my timesheet · this week (live, from time_entries) ────────────────────────
type TsRow = { name: string; tone: string; days: number[] };
const MeTimesheet = ({
  rows,
  dates,
  grand,
  onOpen,
  weekStartsOn,
  perDay,
}: {
  rows: TsRow[];
  dates: number[];
  grand: number;
  /** Absent for a viewer who cannot open /timesheets: no button that only redirects (SH-11). */
  onOpen?: () => void;
  weekStartsOn: number;
  /** The org's standard day: a daily total above it is marked. Was a literal 7.5 (ME-15). */
  perDay: number;
}) => {
  const days = weekdayLabels(weekStartsOn);
  const totals = days.map((_, i) => rows.reduce((a, r) => a + r.days[i], 0));
  // A phone leaves the name ~60px: cut to "Atlas Rebr…", with nowhere else on
  // the page to read the rest. There it wraps instead.
  const phone = useBelow('md');
  // The day columns keep a floor and the project name gives way: with a fixed
  // 130px name column a phone left each day 14px, too narrow for "7.5".
  const grid: CSSProperties = {
    display: 'grid',
    gridTemplateColumns: 'minmax(4.5rem, 1fr) repeat(7, minmax(1.5rem, 3rem))',
  };
  const open = onOpen && (
    <Button
      variant="secondary"
      size="sm"
      iconRight="arrowRight"
      onClick={onOpen}
      className="hf-hit"
    >
      Open full timesheet
    </Button>
  );
  if (rows.length === 0) {
    return (
      <EmptyState title="No hours logged this week yet" action={open}>
        Check in or start a timer and your time shows here.
      </EmptyState>
    );
  }
  // One formatter for every figure here and in the header (fmtDuration 'decimal').
  const figure: CSSProperties = {
    fontSize: 'var(--hf-text-xs)',
    fontFamily: 'var(--hf-font-mono)',
    fontVariantNumeric: 'tabular-nums',
  };
  return (
    <div>
      <div
        style={{
          ...grid,
          fontSize: 'var(--hf-text-2xs)',
          color: 'var(--hf-ink-3)',
          marginBottom: 'var(--hf-space-2)',
        }}
      >
        <div />
        {days.map((d, i) => (
          <div key={i} style={{ textAlign: 'center', fontFamily: 'var(--hf-font-mono)' }}>
            <div>{d}</div>
            <div>{dates[i]}</div>
          </div>
        ))}
      </div>
      {rows.map((r, ri) => (
        <div key={ri} style={{ ...grid, alignItems: 'center', marginBottom: 'var(--hf-space-1)' }}>
          <div
            style={{ display: 'flex', alignItems: 'center', gap: 'var(--hf-space-2)', minWidth: 0 }}
          >
            <span
              style={{
                width: 'var(--hf-space-1)',
                height: 'var(--hf-space-4)',
                borderRadius: 'var(--hf-radius-xs)',
                background: r.tone,
                flexShrink: 0,
              }}
            />
            <span
              // Cut to one line on a desktop: the whole name on hover.
              title={phone ? undefined : r.name}
              style={{
                fontSize: 'var(--hf-text-xs)',
                color: 'var(--hf-ink-2)',
                ...(phone
                  ? { minWidth: 0, overflowWrap: 'anywhere', lineHeight: 'var(--hf-leading-snug)' }
                  : { whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }),
              }}
            >
              {r.name}
            </span>
          </div>
          {r.days.map((v, i) => (
            <div key={i} style={{ padding: '0 var(--hf-space-1)' }}>
              <div
                style={{
                  ...figure,
                  height: 'var(--hf-space-5)',
                  borderRadius: 'var(--hf-radius-sm)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  // The accent at 7–30% by hours worked. It was the retired lime,
                  // under an accent-blue border (ME-15).
                  background:
                    v > 0
                      ? `color-mix(in srgb, var(--hf-accent) ${Math.round(Math.min(30, v * 10 + 7))}%, transparent)`
                      : 'var(--hf-surface)',
                  border: v > 0 ? '1px solid var(--hf-accent-line)' : '1px solid transparent',
                  // ink-3, not ink-4: the zero is text (ME-10). It was a "·"
                  // over a Daily row that said "0", and a dot reads as a
                  // missing value, not none; the full timesheet and the
                  // monthly summary write 0 (TS-08).
                  color: v > 0 ? 'var(--hf-ink)' : 'var(--hf-ink-3)',
                }}
              >
                {hrs(v)}
              </div>
            </div>
          ))}
        </div>
      ))}
      <div
        style={{
          ...grid,
          alignItems: 'center',
          marginTop: 'var(--hf-space-2)',
          paddingTop: 'var(--hf-space-3)',
          borderTop: '1px solid var(--hf-rule)',
        }}
      >
        <Eyebrow>Daily</Eyebrow>
        {totals.map((v, i) => (
          <div
            key={i}
            style={{
              ...figure,
              textAlign: 'center',
              color: v > perDay ? 'var(--hf-accent-text)' : 'var(--hf-ink-2)',
            }}
          >
            {hrs(v)}
          </div>
        ))}
      </div>
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: 'var(--hf-space-3)',
          marginTop: 'var(--hf-space-4)',
        }}
      >
        <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--hf-space-1)' }}>
          <Eyebrow>Week total</Eyebrow>
          <div
            style={{
              fontFamily: 'var(--hf-font-display)',
              fontSize: 'var(--hf-text-2xl)',
              color: 'var(--hf-ink)',
              letterSpacing: 'var(--hf-track-display)',
            }}
          >
            {hrs(grand)}
            <span
              style={{
                fontSize: 'var(--hf-text-sm)',
                color: 'var(--hf-ink-3)',
                fontFamily: 'var(--hf-font-mono)',
                marginLeft: 'var(--hf-space-1)',
              }}
            >
              h
            </span>
          </div>
        </div>
        <div style={{ flex: 1 }} />
        {open}
      </div>
    </div>
  );
};

// ── my leave calendar ─────────────────────────────────────────────────────────
// The shared LeaveCalendar drew a pending day exactly like an approved one, its
// cells were divs that showed the type and reason on mouse hover only, and its
// month buttons had no name (ME-12, ME-V1, ME-V3). This one stands on MonthGrid
// and PeriodStepper: every day is a button that reads out what is on it, and
// choosing a day writes its detail under the grid, where a reason can wrap.
//
// Its marks are the shared set (DayMark, monthGrid.tsx): a leave is a bar,
// filled when approved and outlined while it waits for HR; a holiday is a
// square in its kind's hue, dashed when optional. It had its own, where a
// holiday was a ring and dashed meant "waiting for HR" — on a person's record
// dashed meant a holiday, so HR read one mark two ways (critique 2026-10-05 #1).
type CalLeave = { type: string; status: 'pending' | 'approved'; reason: string | null };
type CalType = { label: string; color: string };
type CalHoliday = Pick<Holiday, 'name' | 'kind'>;

const waitingText = (status: CalLeave['status']) =>
  status === 'pending' ? 'waiting for HR' : 'approved';
const leaveMark = (status: CalLeave['status']) => (status === 'pending' ? 'pending' : 'leave');

const MyLeaveCalendar = ({
  leave,
  types,
  holidays,
  workdays,
  weekStartsOn,
  today,
}: {
  /** Day key → the leave on that day (declined requests left out). */
  leave: Record<string, CalLeave[]>;
  types: Record<string, CalType>;
  /** Day key → the holiday on it, with its kind. */
  holidays: Record<string, CalHoliday>;
  /** The org's working week: a day it doesn't work is hatched. */
  workdays: Workdays | null;
  weekStartsOn: number;
  /** The org's today as a key. It was the browser's (ME-12). */
  today: string;
}) => {
  const now = { y: Number(today.slice(0, 4)), m: Number(today.slice(5, 7)) - 1 };
  const [ym, setYm] = useState(now);
  const [selected, setSelected] = useState<string>();
  // Below md the grid fills the screen and its foot is the bottom of the page,
  // so a day's detail written under it lands out of sight. There it opens in a
  // Dialog instead, a bottom sheet on a phone (UI-CONVENTIONS §8).
  const phone = useBelow('md');
  // Crossing md lets the day go: kept, a day chosen on a wide window would
  // open the sheet unasked when the window narrowed or a phone turned upright.
  const [chosenOnPhone, setChosenOnPhone] = useState(phone);
  if (chosenOnPhone !== phone) {
    setChosenOnPhone(phone);
    setSelected(undefined);
  }
  // "This month" takes itself away when pressed. Left alone, focus falls to
  // <body> and the next Tab starts from the top of the page, so it is handed to
  // the grid's own Tab stop once the month has been drawn.
  const root = useRef<HTMLDivElement>(null);
  const refocus = useRef(false);
  useEffect(() => {
    if (!refocus.current) return;
    refocus.current = false;
    root.current?.querySelector<HTMLElement>('[role="grid"] [tabindex="0"]')?.focus();
  }, [ym]);
  const step = (by: number) =>
    setYm(({ y, m }) => {
      const d = new Date(y, m + by, 1);
      return { y: d.getFullYear(), m: d.getMonth() };
    });
  const typeOf = (id: string): CalType => types[id] ?? { label: id, color: markColor('iris') };

  const prefix = `${ym.y}-${String(ym.m + 1).padStart(2, '0')}`;
  // The key names what this month draws: the kinds of its holidays, not the
  // year's, and the hatch only if the org has a day off in it.
  const monthHolidays = Object.entries(holidays)
    .filter(([k]) => k.startsWith(prefix))
    .map(([, h]) => h);
  const monthDays = new Date(ym.y, ym.m + 1, 0).getDate();
  const anyOff = workingDays(`${prefix}-01`, `${prefix}-${monthDays}`, workdays) < monthDays;
  const inMonth = Object.entries(leave)
    .filter(([k]) => k.startsWith(prefix))
    .flatMap(([, v]) => v);
  const waiting = inMonth.filter((l) => l.status === 'pending').length;
  const approved = inMonth.length - waiting;
  // Keyed while something waits, as on a person's record: a key for a mark
  // that isn't drawn anywhere is noise.
  const anyWaiting = Object.values(leave).some((ls) => ls.some((l) => l.status === 'pending'));

  const picked = selected ? (leave[selected] ?? []) : [];
  const pickedHoliday = selected ? holidays[selected] : undefined;
  const detail = (
    <>
      {picked.map((l, i) => (
        <div key={i} style={{ display: 'flex', flexDirection: 'column', gap: 'var(--hf-space-1)' }}>
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              flexWrap: 'wrap',
              gap: 'var(--hf-space-2)',
              color: 'var(--hf-ink)',
            }}
          >
            <span
              aria-hidden="true"
              style={dayMarkStyle(leaveMark(l.status), typeOf(l.type).color)}
            />
            {typeOf(l.type).label} leave
            <Badge tone={l.status === 'pending' ? 'warning' : 'success'} dot>
              {waitingText(l.status)}
            </Badge>
          </div>
          {l.reason && <div style={{ overflowWrap: 'anywhere' }}>{l.reason}</div>}
        </div>
      ))}
      {pickedHoliday && (
        <div style={{ overflowWrap: 'anywhere', color: 'var(--hf-ink)' }}>
          <DayMark kind={pickedHoliday.kind} name={pickedHoliday.name} />
        </div>
      )}
      {!picked.length && !pickedHoliday && (
        <span style={{ color: 'var(--hf-ink-3)' }}>No leave or holiday on this day.</span>
      )}
    </>
  );
  const detailText: CSSProperties = {
    display: 'flex',
    flexDirection: 'column',
    gap: 'var(--hf-space-2)',
    fontSize: 'var(--hf-text-sm)',
    color: 'var(--hf-ink-2)',
  };

  return (
    <div ref={root} style={{ display: 'flex', flexDirection: 'column', gap: 'var(--hf-space-3)' }}>
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          flexWrap: 'wrap',
          gap: 'var(--hf-space-2) var(--hf-space-3)',
        }}
      >
        {/* The reset is its own button after the stepper rather than the
            stepper's reserved slot: with the slot the row is 320px wide, and the
            card is 246 on a phone. Coming after the arrows, it moves neither. */}
        <PeriodStepper
          label={`${MONTHS[ym.m]} ${ym.y}`}
          unit="month"
          onPrev={() => step(-1)}
          onNext={() => step(1)}
          labelWidth="7.5rem"
        />
        {(ym.y !== now.y || ym.m !== now.m) && (
          <Button
            variant="ghost"
            size="sm"
            className="hf-hit"
            onClick={() => {
              refocus.current = true;
              setYm(now);
            }}
          >
            This month
          </Button>
        )}
        {inMonth.length > 0 && (
          <span style={{ fontSize: 'var(--hf-text-xs)', color: 'var(--hf-ink-3)' }}>
            {[approved && `${nDays(approved)} approved`, waiting && `${nDays(waiting)} waiting`]
              .filter(Boolean)
              .join(' · ')}
          </span>
        )}
      </div>
      <MonthGrid
        year={ym.y}
        month={ym.m}
        weekStartsOn={weekStartsOn}
        label={`${MONTHS[ym.m]} ${ym.y}`}
        compact
        today={today}
        selectedKey={selected}
        onSelectDay={setSelected}
        // The leave day is the cell, in its type's colour (owner, 2026-10-09:
        // the earlier calendar's look). Waiting for HR is outlined only.
        tintOf={(key) => {
          const l = leave[key]?.[0];
          return l ? { color: typeOf(l.type).color, outlined: l.status === 'pending' } : null;
        }}
        // What a day's button reads out after its date. A day the org doesn't
        // work is hatched, as on the Workspace calendar: Sat and Sun were bare
        // here (critique 2026-10-06 #2). Leave is never on one; a holiday may be.
        renderDay={(key) => [
          ...(isWorkday(key, workdays) ? [] : [<DayMark key="off" kind="off" srOnly />]),
          // The tint says it to the eye; this says it to a screen reader.
          ...(leave[key] ?? []).map((l) => (
            <span key={l.type + l.status} className="hf-sr-only">
              {typeOf(l.type).label} leave, {waitingText(l.status)}
            </span>
          )),
          ...(holidays[key]
            ? [<DayMark key="holiday" kind={holidays[key].kind} name={holidays[key].name} srOnly />]
            : []),
        ]}
      />
      {/* The chosen day, in words, then the key at the card's foot: under its
          own rule, with the day's words kept by the grid they describe, as a
          person's record and the Workspace calendar draw theirs. One block, so
          the key's own space above the rule isn't added to the column's gap.
          The day is announced when it changes; on a phone, the hint stays and
          the day opens in the sheet below. */}
      <div>
        <div aria-live={phone ? undefined : 'polite'} style={detailText}>
          {!selected || phone ? (
            <span style={{ color: 'var(--hf-ink-3)' }}>Select a day to read what is on it.</span>
          ) : (
            <>
              <Eyebrow>{fmtDayKey(selected, { weekday: true })}</Eyebrow>
              {detail}
            </>
          )}
        </div>
        <DayMarkKey
          leaveTypes={Object.values(types)}
          pending={anyWaiting}
          holidays={monthHolidays}
          off={anyOff}
        />
      </div>
      {phone && (
        <Dialog
          open={!!selected}
          onClose={() => setSelected(undefined)}
          title={selected ? fmtDayKey(selected, { weekday: true }) : ''}
          size="sm"
        >
          <div style={detailText}>{detail}</div>
        </Dialog>
      )}
    </div>
  );
};

/**
 * Crediting a direct report's hours in one step, with no claim to decide first
 * (bug reports 0eb5bbb4/71ad284c/1786a06b). A reporting manager may hold
 * people:none and never reach /hr, so like the claims queue on this page it goes
 * by relationship, not permission; the trigger already lets the reporting
 * manager grant (20260902120000). Renders nothing unless somebody reports to
 * you; a failed read says so, since it can't say you have nobody (§7).
 */
export function TeamCreditCard({
  uid,
  categories,
  today,
}: {
  uid: string;
  categories: readonly Category[];
  today: string;
}) {
  const [reports, setReports] = useState<DirectReport[] & { failed?: true }>([]);
  const [tries, retry] = useReducer((n: number) => n + 1, 0);
  const [granting, setGranting] = useState<DirectReport | null>(null);
  useEffect(() => {
    let active = true;
    void listDirectReports(uid).then((r) => active && setReports(r));
    return () => {
      active = false;
    };
  }, [uid, tries]);

  const label = 'Credit hours to your team';
  if (reports.failed)
    return (
      <PCard label={label}>
        <LoadError what="your team" onRetry={retry} />
      </PCard>
    );
  if (!reports.length) return null;
  return (
    <PCard label={label}>
      {reports.map((r, i) => (
        <div
          key={r.id}
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: 'var(--hf-space-3)',
            padding: 'var(--hf-space-2) 0',
            borderTop: i ? '1px solid var(--hf-rule)' : undefined,
          }}
        >
          <Avatar name={r.full_name ?? undefined} size={26} />
          <span
            style={{
              flex: 1,
              minWidth: 0,
              overflowWrap: 'anywhere',
              fontSize: 'var(--hf-text-md)',
              color: 'var(--hf-ink)',
            }}
          >
            {r.full_name ?? 'Someone'}
          </span>
          {/* The card's task on a phone; rows are taller than 44px, so the
              areas a finger gets never overlap (UI-CONVENTIONS §8). */}
          <Button
            variant="secondary"
            size="sm"
            icon="award"
            className="hf-hit"
            ariaLabel={`Credit hours to ${r.full_name ?? 'someone'}`}
            onClick={() => setGranting(r)}
          >
            Credit hours
          </Button>
        </div>
      ))}
      {granting && (
        <CompHoursModal
          mode="grant"
          uid={granting.id}
          personName={granting.full_name}
          categories={categories}
          today={today}
          onClose={() => setGranting(null)}
          // Nothing on My Space shows a report's credited hours: the claims
          // queue is pending ones, and a grant lands already approved.
          onFiled={() => {}}
        />
      )}
    </PCard>
  );
}

export function MeScreen() {
  const navigate = useNavigate();
  const { session, profile, roleId, perms } = useAuth();

  // Live contact + bank — the SAME rows HR reads/writes, so the two screens
  // stay in sync. Held in local state (not the auth profile) so edits show
  // immediately. See the Contact type above for the two-table split.
  const [dbContact, setDbContact] = useState<Contact | null>(null);
  // R5 — claims from this person's reporting line. This is the canonical place to
  // decide one: a manager may hold people:none and have no /hr to go to.
  const [compQueue, setCompQueue] = useState<PendingComp[]>([]);
  const [compCats, setCompCats] = useState<Category[]>([]);
  // Your own corrections and claims, for My requests (filed on Timesheets).
  // `failed` rides along from listMine: a read that errored is not "none".
  const [myCorrections, setMyCorrections] = useState<Correction[] & { failed?: true }>([]);
  const [myComp, setMyComp] = useState<CompHours[] & { failed?: true }>([]);
  const reloadCompQueue = useCallback(async () => {
    const id = session?.user.id;
    if (!id) return;
    setCompQueue(await listForManager(id));
  }, [session?.user.id]);
  const [dbBank, setDbBank] = useState<Bank | null>(null);
  // How each group of reads went. Each has its own Retry counter: on a shared
  // one, Retry on the hours card refetched the bank row too, and a bank read
  // that failed that time took an open Save/Cancel edit down with it.
  const [leaveRead, setLeaveRead] = useState<Read>('loading');
  const [contactRead, setContactRead] = useState<Read>('loading');
  const [bankRead, setBankRead] = useState<Read>('loading');
  const [timeRead, setTimeRead] = useState<Read>('loading');
  const bump = (n: number) => n + 1;
  const [contactTry, retryContact] = useReducer(bump, 0);
  const [bankTry, retryBank] = useReducer(bump, 0);
  const [timeTry, retryTime] = useReducer(bump, 0);
  const again = (set: (r: Read) => void, retry: () => void) => () => {
    set('loading');
    retry();
  };

  // Live leave data (types, this user's balances, this user's requests) — the
  // apply form and calendar read these so a submitted request actually persists
  // with a valid leave_type_id and survives reload.
  type DbLeaveType = {
    id: string;
    label: string;
    short: string;
    color: string;
    /** An unpaid type has no entitlement and reduces pay when taken. */
    paid: boolean;
    /** The entitlement when there is no leave_balances row yet — the server's
     *  own fallback (enforce_leave_request: coalesce(allotted, days_per_year, 0)). */
    days_per_year: number | null;
    /** Retired: still labels the requests that cite it, never offered for a new
     *  one (enforce_leave_request refuses it, 20260930170000). */
    archived: boolean;
  };
  type DbRequest = {
    id: string;
    leave_type_id: string;
    start_date: string;
    end_date: string;
    reason: string | null;
    status: 'pending' | 'approved' | 'rejected';
    created_at: string;
    decided_at: string | null;
    /** Why HR declined it, shown in My requests (20260930130000). */
    decline_reason: string | null;
  };
  const [leaveTypesDb, setLeaveTypesDb] = useState<DbLeaveType[]>([]);
  const [balancesDb, setBalancesDb] = useState<Record<string, Balance>>({});
  const [myRequests, setMyRequests] = useState<DbRequest[]>([]);
  // Week start + org timezone, from the one shared source (lib/orgSettings.ts).
  // This screen used to fetch week_start itself and hand it to the leave calendar
  // only, while the timesheet grid inches away ran on a hardcoded Monday.
  const {
    weekStartsOn,
    tz,
    dateFormat,
    hours: orgHours,
    workdays,
    editDay: editDayRollout,
  } = useOrgDates();

  // This week's own time entries + projects + company standard hours, so the
  // profile timesheet + "This week" stat show the signed-in user's real data
  // (same source as TimesheetsScreen) instead of demo rows.
  type TsEntry = { project_id: string | null; started_at: string; seconds: number };
  const [tsEntries, setTsEntries] = useState<TsEntry[]>([]);
  const [tsProjects, setTsProjects] = useState<Record<string, { name: string; tone: string }>>({});
  // This week's REQUIRED hours — the person's contracted day times the days they
  // were actually expected to work. Seeded from the org week so the pre-fetch
  // render is not a hardcoded 40 (it was, and it flashed on every load).
  const [weekTarget, setWeekTarget] = useState(DEFAULT_ORG_HOURS.perWeek);
  // Month worked hours — a separate window from the week grid above.
  const [monthHrs, setMonthHrs] = useState(0);
  // ISO date → the holiday: the calendar draws it by its kind and names it for
  // a chosen day; the working-day helpers only need the keys (`holidays`).
  const [holidayMap, setHolidayMap] = useState<Record<string, CalHoliday>>({});
  const holidays = useMemo<ReadonlySet<string>>(
    () => new Set(Object.keys(holidayMap)),
    [holidayMap],
  );

  // eslint-disable-next-line react-hooks/preserve-manual-memoization -- only matters under the React Compiler, which we don't run
  const loadLeave = useCallback(async () => {
    if (!supabase || !session?.user.id) return;
    const id = session.user.id;
    const [types, bal, reqs, hols] = await Promise.all([
      // Archived types too: they still name the requests that cite them.
      supabase.from('leave_types').select('id, label, short, color, paid, days_per_year, archived'),
      supabase.from('leave_balances').select('leave_type_id, allotted, used').eq('profile_id', id),
      supabase
        .from('leave_requests')
        // Rejected too: My requests is the one place a declined leave shows. The
        // calendar below filters them back out.
        .select(
          'id, leave_type_id, start_date, end_date, reason, status, created_at, decided_at, decline_reason',
        )
        .eq('profile_id', id)
        .order('start_date', { ascending: false }),
      // Every kind counts — leave_working_days() excludes optional holidays too.
      supabase.from('holidays').select('date, name, kind'),
    ]);
    // All four or none: a balance without its types, or a calendar without the
    // holidays, is a wrong answer rather than a partial one.
    if (types.error || bal.error || reqs.error || hols.error) return setLeaveRead('error');
    setHolidayMap(
      Object.fromEntries(
        ((hols.data as Holiday[] | null) ?? []).map((h) => [
          h.date,
          { name: h.name, kind: h.kind },
        ]),
      ),
    );
    setLeaveTypesDb((types.data as DbLeaveType[]) ?? []);
    const map: Record<string, Balance> = {};
    ((bal.data as { leave_type_id: string; allotted: number; used: number }[]) ?? []).forEach(
      (b) => (map[b.leave_type_id] = { allotted: b.allotted, used: b.used }),
    );
    setBalancesDb(map);
    setMyRequests((reqs.data as DbRequest[]) ?? []);
    setLeaveRead('ready');
  }, [session?.user.id]);

  useEffect(() => {
    void loadLeave();
  }, [loadLeave]);
  const reloadLeave = () => {
    setLeaveRead('loading');
    void loadLeave();
  };

  useEffect(() => {
    if (!supabase || !session?.user.id) return;
    const sb = supabase;
    const id = session.user.id;
    let active = true;
    // location + joined live on profiles; the private contact fields on employee_contact.
    void (async () => {
      const [prof, contact, queue, cats, corr, comp] = await Promise.all([
        sb
          .from('profiles')
          .select('location, joined, job_description, employee_no')
          .eq('id', id)
          .maybeSingle(),
        sb.from('employee_contact').select(CONTACT_SELECT).eq('profile_id', id).maybeSingle(),
        listForManager(id),
        listCategories(),
        listMyCorrections(id),
        listMyComp(id),
      ]);
      if (!active) return;
      setCompQueue(queue);
      setCompCats(cats);
      setMyCorrections(corr);
      setMyComp(comp);
      if (prof.error || contact.error) return setContactRead('error');
      const p = prof.data as {
        location: string | null;
        joined: string | null;
        job_description: string | null;
        employee_no: number | null;
      } | null;
      // Spread rather than listing keys: the column list lives in lib/contact.ts
      // now, and hand-listing them here is exactly how this screen fell behind.
      const c = contact.data as Partial<Contact> | null;
      setDbContact({
        ...EMPTY_CONTACT,
        ...c,
        location: p?.location ?? null,
        joined: p?.joined ?? null,
        job_description: p?.job_description ?? null,
        employee_no: p?.employee_no ?? null,
      });
      setContactRead('ready');
    })();
    return () => {
      active = false;
    };
  }, [session?.user.id, contactTry]);

  useEffect(() => {
    if (!supabase || !session?.user.id) return;
    let active = true;
    supabase
      .from('employee_bank')
      .select('holder, account_no, ifsc, bank_name, branch')
      .eq('profile_id', session.user.id)
      .maybeSingle()
      .then(({ data, error }) => {
        if (!active) return;
        if (error) return setBankRead('error');
        const r = data as { [K in keyof Bank]: string | null } | null;
        setDbBank(
          r
            ? {
                holder: r.holder ?? '',
                account_no: r.account_no ?? '',
                ifsc: r.ifsc ?? '',
                bank_name: r.bank_name ?? '',
                branch: r.branch ?? '',
              }
            : null,
        );
        setBankRead('ready');
      });
    return () => {
      active = false;
    };
  }, [session?.user.id, bankTry]);

  // Load this user's own week of time entries + the project directory + org
  // standard hours (mirrors TimesheetsScreen.refresh, scoped to self).
  useEffect(() => {
    if (!supabase || !session?.user.id) return;
    const sb = supabase;
    const id = session.user.id;
    let active = true;
    // The org's week, in the org's calendar. Widened by a day at each end because
    // started_at is a timestamptz and the exact org-day boundary is applied by
    // the column map in `timesheet` below — over-fetching one day is cheaper and
    // more obviously correct than reconstructing an IANA offset by hand.
    const start = addDays(weekStart(weekStartsOn, orgToday(tz)), -1);
    const end = addDays(start, 9);
    const today = orgDateKey(Date.now(), tz);
    const month = monthRange(orgToday(tz));
    void (async () => {
      // The month figure and the week target both come from lib/monthlySummary —
      // the same loader Timesheets and the HR table use, so My Space cannot show a
      // different answer for the same person. It previously summed `started_at`
      // over BROWSER-local month bounds, which is wrong twice: started_at is
      // overwritten on every resume, and the month boundary is the org's.
      const read = await Promise.all([
        sb
          .from('time_entries')
          .select('project_id, started_at, seconds')
          .eq('user_id', id)
          .gte('started_at', start.toISOString())
          .lt('started_at', end.toISOString()),
        sb.from('projects').select('id, name, tone'),
        loadSummaries({
          from: isoDay(weekStart(weekStartsOn, orgToday(tz))),
          to: isoDay(addDays(weekStart(weekStartsOn, orgToday(tz)), 6)),
          tz,
          org: orgHours,
          workdays,
          today,
        }),
        loadSummaries({ from: month.from, to: month.to, tz, org: orgHours, workdays, today }),
      ]).catch(() => null);
      if (!active) return;
      // A failed summary is no figure, not 0h against the org's week: loadSummaries
      // answers all eight of its reads or none (SummaryRows.failed).
      if (!read || read[0].error || read[1].error || read[2].failed || read[3].failed)
        return setTimeRead('error');
      const [te, pr, weekRows, monthRows] = read;
      setTsEntries((te.data as TsEntry[]) ?? []);
      const mine = (monthRows as MonthSummary[]).find((r) => r.profileId === id) ?? null;
      setMonthHrs(mine?.workedHours ?? 0);
      const pm: Record<string, { name: string; tone: string }> = {};
      ((pr.data as { id: string; name: string; tone: string }[]) ?? []).forEach(
        (p) => (pm[p.id] = { name: p.name, tone: p.tone }),
      );
      setTsProjects(pm);
      // The week's TARGET, not a flat standard week: holidays and approved paid
      // leave are already out of it, so someone on leave Monday is not told they
      // are a day short.
      setWeekTarget(
        (weekRows as MonthSummary[]).find((r) => r.profileId === id)?.requiredHours ??
          orgHours.perWeek,
      );
      setTimeRead('ready');
    })();
    return () => {
      active = false;
    };
    // useOrgDates() resolves asynchronously and hands back column defaults first,
    // so this MUST re-run when the real values land — otherwise the week target
    // stays on the fallback for the life of the screen.
  }, [session?.user.id, tz, weekStartsOn, orgHours, workdays, timeTry]);

  // Build the weekly project × day grid from raw stored seconds (no live/elapsed
  // math — that only applies to the day-clock, not time_entries).
  const timesheet = useMemo(() => {
    const start = weekStart(weekStartsOn, orgToday(tz));
    // Bucket by the org's calendar key, not by a millisecond delta. A delta is
    // wrong on the 23- and 25-hour days of a DST change, and the clamp it needed
    // (`Math.min(6, Math.max(0, …))`) silently filed strays under column 0.
    const col = weekColumns(weekStartsOn, orgToday(tz));
    const byProj: Record<string, number[]> = {};
    let grand = 0;
    for (const e of tsEntries) {
      const di = col.get(orgDateKey(e.started_at, tz));
      if (di === undefined) continue; // outside this week — from the ±1 day over-fetch
      const pid = e.project_id ?? 'general';
      const hrs = e.seconds / 3600;
      (byProj[pid] ??= [0, 0, 0, 0, 0, 0, 0])[di] += hrs;
      grand += hrs;
    }
    const rows: TsRow[] = Object.entries(byProj).map(([pid, days]) => ({
      name: tsProjects[pid]?.name ?? (pid === 'general' ? 'General' : 'Project'),
      // A project's colour bar is a mark: markColor, the same one Timesheets uses.
      tone: markColor(tsProjects[pid]?.tone ?? 'iris'),
      days,
    }));
    const dates = Array.from({ length: 7 }, (_, i) => addDays(start, i).getDate());
    return { rows, grand, dates, label: weekHeader(start, weekStartsOn) };
  }, [tsEntries, tsProjects, weekStartsOn, tz]);

  // Identity + contact + JD come live from the profile/bank rows; leave loads
  // live below; the weekly timesheet comes from `timesheet` (time_entries).
  const me: MeRecord = useMemo(
    () => ({
      // Every field comes from the signed-in profile or is explicitly blank. There
      // is deliberately no demo fallback: an employee whose HR record is still
      // incomplete used to read as "EMP-004 · Operations Director · Strategy ·
      // joined 2019" — someone else's identity. Unset means unset.
      name: profile?.full_name ?? session?.user.email ?? '—',
      systemRole: roleId ?? '',
      role: profile?.title ?? '—',
      dept: profile?.department ?? '—',
      // R4: server-assigned, immutable, and the identifier payroll and statutory
      // records hang off once a name has been erased.
      id: dbContact?.employee_no != null ? String(dbContact.employee_no) : '—',
      email: session?.user.email ?? '',
      personalEmail: dbContact?.personal_email ?? '',
      location: dbContact?.location ?? '',
      mobile: dbContact?.mobile ?? '',
      address: dbContact?.address ?? '',
      dob: dbContact?.dob ?? '',
      emergencyName: dbContact?.emergency_name ?? '',
      emergencyRelation: dbContact?.emergency_relation ?? '',
      emergencyPhone: dbContact?.emergency_phone ?? '',
      joined: dbContact?.joined ?? '',
      jd: dbContact?.job_description ?? '',
      // No row is no bank details. The holder used to default to the profile
      // name, which read as a saved value and was never written.
      bank: dbBank ?? EMPTY_BANK,
    }),
    [profile, roleId, session, dbContact, dbBank],
  );

  // One contact field. location lives on profiles; the rest (private PII) on
  // employee_contact, gated to self + HR. Through write(), so a save RLS filtered
  // out is a refusal on the field, and the value on screen only changes once the
  // row has: it used to change first and revert under a rose toast (ME-07).
  const saveContact = async (
    delta: Partial<Omit<Contact, 'joined' | 'job_description' | 'employee_no'>>,
  ): Promise<SaveResult> => {
    if (!supabase || !session?.user.id) return { error: 'Not signed in.' };
    const id = session.user.id;
    const r =
      'location' in delta
        ? await write(supabase.from('profiles').update({ location: delta.location }).eq('id', id))
        : await write(
            supabase
              .from('employee_contact')
              // Filtered: only real employee_contact columns reach the upsert.
              .upsert({ profile_id: id, ...contactCols(delta) }, { onConflict: 'profile_id' }),
            'profile_id',
          );
    if (!r.ok) return notSaved(r);
    setDbContact((p) => ({ ...EMPTY_CONTACT, ...p, ...delta }));
    return {};
  };

  // One bank column (self-RLS: the owner-guard migration). Only that column is
  // sent: the whole row used to be, rebuilt from what this screen had loaded.
  const saveBank = async (col: keyof Bank, raw: string): Promise<SaveResult> => {
    if (!supabase || !session?.user.id) return { error: 'Not signed in.' };
    const clean = cleanBank(col, raw);
    if ('error' in clean) return clean;
    const r = await write(
      supabase
        .from('employee_bank')
        .upsert({ profile_id: session.user.id, [col]: clean.value }, { onConflict: 'profile_id' }),
      'profile_id',
    );
    if (!r.ok) return notSaved(r);
    setDbBank((b) => ({ ...EMPTY_BANK, ...b, [col]: clean.value }));
    return {};
  };

  const roleT = TONE[roleTone(me.systemRole)];
  // Below md the header is cut to the first screen's top half, so Apply for
  // leave starts above the fold: a 72px avatar stacked over the name, then four
  // stats, filled a 390 screen before any card you act on.
  const phone = useBelow('md');
  const today = orgToday(tz);
  const ten = tenure(me.joined, today);
  // Each type's balance, as the server counts it: with no leave_balances row the
  // entitlement is the type's days_per_year, not 0 — so a paid type nobody has
  // seeded a row for read "0/0 left" here while the server would approve it.
  const leaveBalances: Record<string, Balance> = Object.fromEntries(
    leaveTypesDb.map((t) => [
      t.id,
      {
        allotted: balancesDb[t.id]?.allotted ?? t.days_per_year ?? 0,
        used: balancesDb[t.id]?.used ?? 0,
      },
    ]),
  );
  // What can be asked for now. An archived type's balance can't be spent, and
  // the server refuses a new request on it.
  const offered = leaveTypesDb.filter((t) => !t.archived);
  // Per PAID type, never pooled: "31 / 44 days" added Annual, Sick and Casual
  // into a figure nobody can spend as one (ME-14). Unpaid types have no
  // entitlement to be "left" of.
  const leaveLeft = offered
    .filter((t) => t.paid)
    .map((t) => ({
      label: t.label,
      left: leaveBalances[t.id].allotted - leaveBalances[t.id].used,
    }));
  // Comp off beside them (owner, 2026-10-09: "merge comp off leaves into leave
  // balances"): what can still be earned this year against the yearly limit.
  const compOff = useCompOff(session?.user.id, today.getFullYear());
  const leaveLeftAll =
    compOff?.left != null ? [...leaveLeft, { label: 'Comp off', left: compOff.left }] : leaveLeft;

  // Persist a leave request, then refresh so it shows in the calendar + HR queue.
  const handleApply = async ({
    type,
    start,
    end,
    reason,
    dayCount,
    halfDay,
  }: ApplyPayload): Promise<{ error?: string }> => {
    if (!supabase || !session?.user.id) return { error: 'Not signed in.' };
    const { error } = await supabase.from('leave_requests').insert({
      profile_id: session.user.id,
      leave_type_id: type,
      start_date: start,
      end_date: end,
      day_count: dayCount,
      half_day: halfDay,
      reason,
      status: 'pending',
    });
    if (error) return { error: error.message };
    await loadLeave();
    return {};
  };

  // Inline object status for the Apply card: how many of your requests are
  // still with HR.
  const myPending = myRequests.filter((r) => r.status === 'pending').length;

  // My requests is three reads: the leave above, and the corrections and claims
  // that come in with the contact read. Either of those failing leaves the list
  // unknown, not shorter (§7), and its Retry re-runs the read that failed.
  const requestsRead: Read =
    leaveRead !== 'ready' || !(myCorrections.failed || myComp.failed)
      ? leaveRead
      : contactRead === 'loading'
        ? 'loading'
        : 'error';
  const retryRequests = leaveRead === 'error' ? reloadLeave : again(setContactRead, retryContact);

  // Approved and waiting requests, a day at a time, each carrying its status: the
  // calendar drew both the same, so people planned around leave nobody had
  // approved (ME-12). Declined ones show in My requests only.
  const calLeave: Record<string, CalLeave[]> = {};
  for (const r of myRequests) {
    if (r.status === 'rejected') continue;
    // The chargeable days, by the server's rule (org workdays less holidays).
    for (const d of workingDayList(r.start_date, r.end_date, workdays, holidays)) {
      (calLeave[d] ??= []).push({ type: r.leave_type_id, status: r.status, reason: r.reason });
    }
  }
  const calTypes: Record<string, CalType> = Object.fromEntries(
    leaveTypesDb.map((t) => [t.id, { label: t.label, color: t.color }]),
  );

  // Last in the header on a phone, nearest the leave tiles it sums up. Moved in
  // the DOM, not by CSS order, so it is read where it is seen.
  const leaveStat = (
    <KpiTile
      label="Leave left"
      size={phone ? 'sm' : 'md'}
      value={
        leaveRead !== 'ready' || leaveLeft.length === 0 ? null : (
          // Wraps between types: run together with margins, the figures
          // had no break between them and a fourth type ran off a phone.
          <span
            style={{
              display: 'inline-flex',
              flexWrap: 'wrap',
              gap: 'var(--hf-space-2) var(--hf-space-3)',
            }}
          >
            {leaveLeftAll.map((t) => (
              <span key={t.label}>
                {t.left}
                <span style={statUnit}>{t.label.toLowerCase()}</span>
              </span>
            ))}
          </span>
        )
      }
      // A read that worked but found no paid type has nothing to wait for.
      note={
        leaveRead === 'ready' && leaveLeft.length === 0 ? 'no paid leave types' : 'days this year'
      }
    />
  );

  return (
    <div
      style={{
        // A 16px gutter on a phone: 24 cost each card 16px of a 390 screen.
        padding: phone ? 'var(--hf-space-4)' : 'var(--hf-space-5)',
        display: 'flex',
        flexDirection: 'column',
        gap: 'var(--hf-space-4)',
        maxWidth: 1240,
        margin: '0 auto',
      }}
    >
      {/* Identity header */}
      <div
        className="hf-glass"
        style={{
          borderRadius: 'var(--hf-radius-card)',
          padding: 0,
          overflow: 'hidden',
          position: 'relative',
        }}
      >
        <div
          style={{
            position: 'absolute',
            inset: 0,
            background: `radial-gradient(120% 140% at 92% -20%, ${roleT.bg}, transparent 55%)`,
            pointerEvents: 'none',
          }}
        />
        <div
          style={{
            position: 'relative',
            padding: phone ? 'var(--hf-pad-card)' : 'var(--hf-pad-card-lg)',
            display: 'flex',
            alignItems: phone ? 'flex-start' : 'center',
            gap: phone ? 'var(--hf-space-4)' : 'var(--hf-space-5)',
            flexWrap: 'wrap',
          }}
        >
          <Avatar name={me.name} size={phone ? 48 : 72} />
          {/* Beside the avatar on a phone, not wrapped under it. */}
          <div style={{ flex: 1, minWidth: phone ? 0 : 'min(100%, 14rem)' }}>
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: 'var(--hf-space-3)',
                flexWrap: 'wrap',
              }}
            >
              {/* h2: the Topbar's title (SCREEN_TITLE.me, "My space") is the page's one h1. */}
              <h2
                style={{
                  fontFamily: 'var(--hf-font-display)',
                  fontSize: 'var(--hf-text-2xl)',
                  fontWeight: 'var(--hf-weight-medium)',
                  margin: 0,
                  letterSpacing: 'var(--hf-track-display)',
                  color: 'var(--hf-ink)',
                  overflowWrap: 'anywhere',
                }}
              >
                {me.name}
              </h2>
              {/* The shared Badge on roleTone: the hand-built pill appended hex
                  alpha to a tone that isn't a hex, so viewer and custom roles
                  had no fill at all (ME-15). */}
              <Badge tone={roleTone(me.systemRole)} dot>
                {roleLabel(me.systemRole)}
              </Badge>
            </div>
            <div
              style={{
                fontSize: 'var(--hf-text-md)',
                color: 'var(--hf-ink-2)',
                marginTop: 'var(--hf-space-2)',
              }}
            >
              {me.role} · {me.dept}
            </div>
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                // Tight between wrapped lines: the email and the location read
                // as two loose rows 16px apart on a phone.
                gap: 'var(--hf-space-1) var(--hf-space-4)',
                marginTop: 'var(--hf-space-3)',
                fontSize: 'var(--hf-text-xs)',
                color: 'var(--hf-ink-3)',
                fontFamily: 'var(--hf-font-mono)',
                flexWrap: 'wrap',
              }}
            >
              <span style={headerFact}>
                <Icon name="user" size={12} />
                {me.id}
              </span>
              <span style={{ ...headerFact, overflowWrap: 'anywhere' }}>
                <Icon name="inbox" size={12} />
                {me.email}
              </span>
              {/* A place is words: the number and the address keep the mono. */}
              {me.location && (
                <span style={{ ...headerFact, fontFamily: 'var(--hf-font-ui)' }}>
                  <Icon name="pin" size={12} />
                  {me.location}
                </span>
              )}
            </div>
          </div>
          {/* Wraps, so at a narrow width the stats drop under the name instead of
              being cut off ("THIS WEE", ME-01). No rule on the left: it only made
              sense while the row sat beside the name. Inside this card's
              <Surface> the KpiTiles are bare figures, not tiles in a card. */}
          <Surface>
            <div
              style={{
                display: 'flex',
                flexWrap: 'wrap',
                gap: phone
                  ? 'var(--hf-space-4) var(--hf-space-5)'
                  : 'var(--hf-space-4) var(--hf-space-6)',
                // A row of its own on a phone: as a flex item it could take the
                // avatar's line and squeeze the name to nothing.
                width: phone ? '100%' : undefined,
              }}
            >
              <KpiTile
                label="Tenure"
                size={phone ? 'sm' : 'md'}
                value={
                  !ten ? null : (
                    <>
                      {ten.yrs}
                      <span style={{ ...statUnit, marginRight: 'var(--hf-space-2)' }}>yr</span>
                      {ten.mos}
                      <span style={statUnit}>mo</span>
                    </>
                  )
                }
                // Only a read that answered can say the date isn't set.
                note={
                  me.joined
                    ? `joined ${fmtDate(me.joined, dateFormat)}`
                    : contactRead === 'ready'
                      ? 'join date not set'
                      : undefined
                }
              />
              {!phone && leaveStat}
              <KpiTile
                label="This week"
                size={phone ? 'sm' : 'md'}
                // The stub until the hours are in: not measured is not zero (§7).
                value={timeRead === 'ready' ? hrs(timesheet.grand) : null}
                unit="h"
                // The label says which week; the note says what of. "of 40h
                // this week" under "This week" said it twice.
                note={timeRead === 'ready' ? `of ${hrs(weekTarget)}h` : undefined}
              />
              <KpiTile
                label="This month"
                size={phone ? 'sm' : 'md'}
                value={timeRead === 'ready' ? hrs(monthHrs) : null}
                unit="h"
                // A month's name is a proper noun: it read "october".
                note={MONTHS[today.getMonth()]}
              />
              {phone && leaveStat}
            </div>
          </Surface>
        </div>
      </div>

      {/* Two column stacks, not rows of paired cards: a row is as tall as its
          taller card, and the shorter one left a hole beside it.

          What you act on comes first: leave, your requests and its calendar in
          the first column, this week's hours at the top of the second, and the
          records people rarely change (contact, bank, the job description)
          after them. It was laid out like an HR file, with Apply for leave
          under the bank details and Submit below the fold (ME-11).

          auto-fit with a floored track: the columns are equal, never sized by
          their content (a long request reason used to widen one and squeeze the
          other), and they fold to one when there isn't room for two (ME-01). */}
      <div
        style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(min(100%, 22rem), 1fr))',
          gap: 'var(--hf-space-4)',
          alignItems: 'start',
        }}
      >
        <div style={stack}>
          {/* Renders nothing unless somebody reports to you and has claimed time.
              No attention badge: /me has no module to hang one on, and the
              notification already brought them here. */}
          {/* Renders nothing unless someone who reports to you forgot to check
              out and the system closed their day. Same posture as the claims
              below: the notice brought them here (owner, 2026-10-13). */}
          {/* FR-8.7: policies addressed to you, unacknowledged first. Renders
              nothing when there are none; the account card carries the count. */}
          {session?.user.id && <MyPoliciesCard uid={session.user.id} />}
          {session?.user.id && <TeamDaysToFix tz={tz} editDay={editDayRollout !== 'off'} />}
          {session?.user.id && <TeamDayEdits uid={session.user.id} />}
          {session?.user.id && (
            <CompHoursDecisions
              rows={compQueue}
              uid={session.user.id}
              categories={compCats}
              title="Compensated hours awaiting your decision"
              onChanged={() => void reloadCompQueue()}
            />
          )}
          {/* Inline object status, not a queue roll-up (docs/UI-CONVENTIONS.md):
              it is about YOUR records, and nobody is waiting on you. */}
          <PCard
            label="Apply for leave"
            right={
              myPending > 0 ? (
                <Badge tone="warning" dot>
                  {myPending} waiting for HR
                </Badge>
              ) : undefined
            }
          >
            <Loaded read={leaveRead} what="your leave" onRetry={reloadLeave}>
              <ApplyLeaveForm
                onSubmit={handleApply}
                balances={leaveBalances}
                types={offered}
                workdays={workdays}
                holidays={holidays}
                canBackdate={can(perms, 'people', 'full')}
                today={isoDay(today)}
              />
            </Loaded>
          </PCard>
          {/* MyRequests says "Nothing requested yet" for an empty list, which
              only a read that answered may say. */}
          {requestsRead === 'ready' ? (
            <MyRequests
              leave={myRequests}
              leaveTypes={leaveTypesDb}
              corrections={myCorrections}
              comp={myComp}
              categories={compCats}
              // The badge and the calendar read myRequests too; without this a
              // withdrawn leave stayed "waiting for HR" on both until a reload.
              // Corrections and claims show only here, where the row hides at once.
              onWithdrawn={(kind) => {
                if (kind === 'leave') void loadLeave();
              }}
            />
          ) : (
            <PCard label="My requests">
              <Loaded read={requestsRead} what="your requests" onRetry={retryRequests} />
            </PCard>
          )}
          <PCard label="Leave calendar">
            <Loaded read={leaveRead} what="your leave calendar" onRetry={reloadLeave}>
              <MyLeaveCalendar
                leave={calLeave}
                types={calTypes}
                holidays={holidayMap}
                workdays={workdays}
                weekStartsOn={weekStartsOn}
                today={isoDay(today)}
              />
            </Loaded>
          </PCard>
          {/* Last in the column, not under the claims queue: that queue shows
              only while a claim waits, but this shows a row per report on every
              visit, and above Apply for leave it pushed a manager's own leave
              below the fold on a phone (ME-11). */}
          {session?.user.id && (
            <TeamCreditCard uid={session.user.id} categories={compCats} today={isoDay(today)} />
          )}
          {/* Sent reports from the people below you; nothing for someone nobody reports to. */}
          {session?.user.id && (
            <TeamReports uid={session.user.id} tz={tz} dateFormat={dateFormat} />
          )}
        </div>

        <div style={stack}>
          {/* Today's report to send: an action, so it heads the column. */}
          {session?.user.id && (
            <MyDailyReport
              uid={session.user.id}
              today={isoDay(today)}
              tz={tz}
              dateFormat={dateFormat}
            />
          )}
          {/* "Week of 13 Sep" in the UI face, as Timesheets' own stepper
              says it: in mono the words read as code. */}
          <PCard
            label="My timesheet"
            right={
              <span style={{ fontSize: 'var(--hf-text-xs)', color: 'var(--hf-ink-3)' }}>
                {timesheet.label}
              </span>
            }
          >
            <Loaded
              read={timeRead}
              what="this week’s hours"
              onRetry={again(setTimeRead, retryTime)}
            >
              <MeTimesheet
                rows={timesheet.rows}
                dates={timesheet.dates}
                grand={timesheet.grand}
                // The route's own check (App's Guard), so the CTA never leads to
                // a refusal (SH-11).
                onOpen={canOpen(perms, 'timesheets') ? () => navigate('/timesheets') : undefined}
                weekStartsOn={weekStartsOn}
                perDay={orgHours.perDay}
              />
            </Loaded>
          </PCard>

          {/* Hours, then what they came to. Renders nothing until a month has
              actually been closed — a figure that can still move is not a
              statement, and the live hours are directly above. */}
          {session?.user.id && <MyPayslip uid={session.user.id} />}
          <PCard label="My contact details">
            <Loaded
              read={contactRead}
              what="your contact details"
              onRetry={again(setContactRead, retryContact)}
            >
              {/* Paired two-up rather than eight stacked rows. These are short values
              in a column that already has the width for them; stacked, the card
              was ~330px tall against bank's ~150 and that mismatch was the hole
              in the old layout. Address stays full-width because it is multiline. */}
              <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--hf-space-4)' }}>
                {/* How these fields work, said once: the values are buttons that
                  look like text, and nothing told you a change saves when you
                  leave the field (ME-07). */}
                <p style={cardHint}>
                  Select a value to change it. A change saves when you leave the field; Esc cancels.
                </p>
                <div style={pair}>
                  <EditableField
                    label="Mobile"
                    value={me.mobile}
                    mono={!!me.mobile}
                    type="tel"
                    placeholder="Add mobile"
                    onSaveAsync={(v) => saveContact({ mobile: v })}
                  />
                  <EditableField
                    label="Personal email"
                    value={me.personalEmail}
                    type="email"
                    placeholder="Add personal email"
                    onSaveAsync={(v) => saveContact({ personal_email: v })}
                  />
                </div>
                <EditableField
                  label="Address"
                  value={me.address}
                  multiline
                  placeholder="Add address"
                  onSaveAsync={(v) => saveContact({ address: v })}
                />
                <div style={pair}>
                  <EditableField
                    label="Location"
                    value={me.location}
                    placeholder="Add location"
                    onSaveAsync={(v) => saveContact({ location: v })}
                  />
                  {/* Collected at first-login intake. HR can see and correct these, so
                  the employee they belong to has to be able to as well. */}
                  <EditableField
                    label="Date of birth"
                    value={me.dob}
                    mono={!!me.dob}
                    type="date"
                    placeholder="Add date of birth"
                    // dob is a date column — '' is not a valid date, so clearing sends null.
                    onSaveAsync={(v) => saveContact({ dob: v || null })}
                  />
                </div>
                <div style={pair}>
                  <EditableField
                    label="Emergency contact"
                    value={me.emergencyName}
                    placeholder="Add a name"
                    onSaveAsync={(v) => saveContact({ emergency_name: v })}
                  />
                  <EditableField
                    label="Relationship"
                    value={me.emergencyRelation}
                    placeholder="Add relationship"
                    onSaveAsync={(v) => saveContact({ emergency_relation: v })}
                  />
                </div>
                <EditableField
                  label="Emergency phone"
                  value={me.emergencyPhone}
                  mono={!!me.emergencyPhone}
                  type="tel"
                  placeholder="Add emergency phone"
                  onSaveAsync={(v) => saveContact({ emergency_phone: v })}
                />
              </div>
            </Loaded>
          </PCard>
          {/* Who can read it, in SensitiveMark's words: the row's owner
              (bank_self_read) and full People access (bank_rw). It was a
              "confidential" badge, which named nobody (critique 2026-10-05 #7).
              Every field here is an explicit Save: a bank detail is rung 2 of
              the stakes ladder (§5), and these saved on blur (ME-05). */}
          <PCard label="Bank details" right={<SensitiveMark label="Only you, HR and admins" />}>
            {/* Not editable until the row is in: a save made over a failed read
                would be typed against a blank that isn't one. */}
            <Loaded
              read={bankRead}
              what="your bank details"
              onRetry={again(setBankRead, retryBank)}
            >
              <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--hf-space-4)' }}>
                <p style={cardHint}>
                  Select a value to change it. Nothing here saves until you Save.
                </p>
                <EditableField
                  label="Account holder"
                  value={me.bank.holder}
                  placeholder="Add account holder"
                  commitOn="explicit"
                  onSaveAsync={(v) => saveBank('holder', v)}
                />
                <BankAccount value={me.bank.account_no} onSave={(v) => saveBank('account_no', v)} />
                <div style={pair}>
                  {/* "IFSC / SWIFT" alone left out what it is: a code for the bank,
                    which outside India is the SWIFT/BIC (ME-16). */}
                  <EditableField
                    label="Bank code (IFSC or SWIFT)"
                    value={me.bank.ifsc}
                    mono={!!me.bank.ifsc}
                    placeholder="Add bank code"
                    commitOn="explicit"
                    onSaveAsync={(v) => saveBank('ifsc', v)}
                  />
                  <EditableField
                    label="Branch"
                    value={me.bank.branch}
                    placeholder="Add branch"
                    commitOn="explicit"
                    onSaveAsync={(v) => saveBank('branch', v)}
                  />
                </div>
                <EditableField
                  label="Bank"
                  value={me.bank.bank_name}
                  placeholder="Add bank"
                  commitOn="explicit"
                  onSaveAsync={(v) => saveBank('bank_name', v)}
                />
              </div>
            </Loaded>
          </PCard>
          {/* FR-8.6: yours to record; you and HR are reminded before each expires. */}
          {session?.user.id && (
            <CertificatesCard profileId={session.user.id} canEdit label="My certificates" />
          )}
          <PCard label="My role · job description">
            <div className="hf-scroll" style={{ maxWidth: 720, maxHeight: 320, overflowY: 'auto' }}>
              {/* The shared renderer HR's view of this same text uses; the private
                  copy here would have drifted from it (ME-18). */}
              <Loaded
                read={contactRead}
                what="your job description"
                onRetry={again(setContactRead, retryContact)}
              >
                {me.jd.trim() ? (
                  <Markdown source={me.jd} />
                ) : (
                  <EmptyState title="No job description yet">Your HR team will add it.</EmptyState>
                )}
              </Loaded>
            </div>
          </PCard>
        </div>
      </div>
    </div>
  );
}
